import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Shield, Globe, ChevronRight, ExternalLink, Flame } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import PageTransition from '../components/layout/PageTransition';
import ScrollReveal, { StaggerContainer, StaggerItem } from '../components/layout/ScrollReveal';
import BootLine from '../components/layout/BootLine';
import Telemetry from '../components/layout/Telemetry';
import { getProjects } from '../lib/api';

const CAPABILITIES = [
  { icon: Zap, label: 'Throughput', desc: 'High-performance infrastructure built for scale' },
  { icon: Shield, label: 'Reliability', desc: 'Enterprise-grade uptime and fault tolerance' },
  { icon: Globe, label: 'Global Reach', desc: 'Multi-region deployment and distribution' },
];

function EngineStatusCard({ projects }) {
  return (
    <div className="hud-card border border-white/10 bg-white/[0.03] p-6 relative overflow-hidden">
      <div className="absolute inset-0 scanline-bg" />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-3"><Flame className="h-3 w-3 text-[#E8722A]" /><span className="kicker">Engine Status</span></div>
        <div className="space-y-4">
          {projects.map((project, idx) => (
            <div key={project.id} className="flex items-center gap-3 p-3 rounded-[4px] border border-[rgba(232,114,42,0.16)] bg-[rgba(232,114,42,0.03)]">
              <span className="font-mono text-[10px] text-white/30 tabular-nums">{String(idx + 1).padStart(2, '0')}</span>
              <div className={`w-2 h-2 rounded-full ${project.status?.toLowerCase() === 'live' ? 'bg-[#E8722A] status-pulse' : 'bg-white/30'}`} />
              <span className="text-sm font-medium text-white/90">{project.name}</span>
              <StatusBadge status={project.status} className="ml-auto" />
            </div>
          ))}
          {Array.from({ length: Math.max(0, 4 - projects.length) }).map((_, i) => (
            <div key={`slot-${i}`} className="flex items-center gap-3 p-3 rounded-[4px] border border-white/6 bg-white/[0.02]">
              <span className="font-mono text-[10px] text-white/20 tabular-nums">{String(projects.length + i + 1).padStart(2, '0')}</span>
              <div className="w-2 h-2 rounded-full bg-white/20" />
              <span className="text-sm text-white/30 font-mono">Signal detected</span>
              <Badge className="ml-auto bg-white/8 text-white/40 border-white/12 text-[10px]">PENDING</Badge>
            </div>
          ))}
        </div>
        <div className="mt-5 pt-4 border-t border-white/8">
          <div className="flex justify-between text-xs text-white/40">
            <span>Systems Active</span>
            <span className="font-mono text-[#E8722A]">{projects.filter(p => p.status?.toLowerCase() === 'live').length} / {Math.max(4, projects.length)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const liveCount = projects.filter((p) => p.status?.toLowerCase() === 'live').length;

  useEffect(() => {
    document.title = 'Visualte - Digital Infrastructure';
    getProjects().then((res) => setProjects(res.data.projects)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <PageTransition>
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden hud-sweep" data-testid="hero-section">
        <div className="absolute inset-0 bg-fire-fog" />
        <div className="absolute inset-0 grid-overlay" />
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 lg:pt-36 pb-14 sm:pb-20 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-10 lg:gap-16 items-center">
            <div>
              <ScrollReveal hero delay={0}>
                <BootLine className="mb-6" />
                <div className="flex items-center gap-2 mb-5">
                  <Flame className="h-4 w-4 text-[#E8722A]" />
                  <span className="kicker">Digital Infrastructure Engine</span>
                </div>
              </ScrollReveal>
              <ScrollReveal hero delay={0.1}>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.04em] leading-[0.95] mb-6">
                  Power systems.<br />Connect brands.<br />Scale products.
                </h1>
              </ScrollReveal>
              <ScrollReveal hero delay={0.2}>
                <p className="text-base md:text-lg text-white/72 leading-relaxed max-w-lg mb-8">
                  Visualte is the foundational engine that drives and supports digital businesses. From product launch to global scale.
                </p>
              </ScrollReveal>
              <ScrollReveal hero delay={0.3}>
                <div className="flex flex-wrap gap-3">
                  <Link to="/ecosystem">
                    <Button data-testid="hero-primary-cta-button" className="btn-primary-gradient btn-shimmer font-semibold h-12 px-6 text-base rounded-lg">
                      Explore Ecosystem <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button variant="outline" data-testid="hero-secondary-cta-button" className="bg-white/5 text-white/85 border-white/10 hover:bg-white/8 hover:border-white/18 h-12 px-6 text-base">
                      Discuss Business
                    </Button>
                  </Link>
                </div>
              </ScrollReveal>
              <ScrollReveal hero delay={0.4}>
                <div className="flex flex-wrap gap-3 mt-10">
                  {CAPABILITIES.map((cap) => (
                    <div key={cap.label} className="flex items-center gap-2 px-4 py-2 rounded-[4px] border border-white/10 bg-white/[0.03]">
                      <cap.icon className="h-3.5 w-3.5 text-[#E8722A]" />
                      <span className="text-xs font-medium text-white/70">{cap.label}</span>
                    </div>
                  ))}
                </div>
              </ScrollReveal>
              <ScrollReveal hero delay={0.5}>
                <Telemetry
                  bordered
                  className="mt-8"
                  items={[{ k: 'NODES', v: `${liveCount} ACTIVE`, tone: 'accent' }]}
                  data-testid="hero-telemetry"
                />
              </ScrollReveal>
              {/* Engine Status Card — shown below tags on tablet only, tappable to scroll to ecosystem */}
              <ScrollReveal hero delay={0.5}>
                <div className="hidden md:block lg:hidden mt-8 cursor-pointer" onClick={() => {
                  const el = document.getElementById('ecosystem-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}>
                  <EngineStatusCard projects={projects} />
                </div>
              </ScrollReveal>
            </div>
            <ScrollReveal hero delay={0.3} direction="right" distance={30}>
              {/* Engine Status Card — right column on desktop only */}
              <div className="relative hidden lg:block">
                <EngineStatusCard projects={projects} />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Ecosystem */}
      <section id="ecosystem-section" className="py-16 sm:py-20 lg:py-28 relative" data-testid="ecosystem-preview-section">
        <div className="absolute inset-0 noise-overlay" />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal>
            <div className="hud-index mb-3">SEC.02 // ECOSYSTEM</div>
            <span className="kicker">Powered by Visualte</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.04em] leading-[0.95] mt-4 mb-4">The ecosystem</h2>
            <p className="text-base text-white/72 leading-relaxed max-w-lg mb-12">Products and platforms running on Visualte infrastructure. Reliable. Scalable. Always on.</p>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {projects.map((project) => (
              <StaggerItem key={project.id}>
                <Link to={`/powered/${project.slug}`} data-testid="ecosystem-project-card">
                  <div className="hud-card border border-white/10 bg-white/[0.03] p-6 h-full card-hover-glow group">
                    <span className="kicker">{project.category || 'Platform'}</span>
                    <h3 className="text-xl md:text-2xl font-semibold tracking-[-0.02em] mt-2 mb-2 group-hover:text-[#E8722A] transition-colors duration-200">{project.name}</h3>
                    <StatusBadge status={project.status} />
                    <p className="text-sm text-white/55 leading-relaxed mt-3 mb-4 line-clamp-2">{project.tagline}</p>
                    <div className="flex items-center gap-1 text-xs text-[#E8722A] group-hover:gap-2 transition-all duration-200"><span>Explore</span><ChevronRight className="h-3 w-3" /></div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
            {[1, 2].map((i) => (
              <StaggerItem key={`slot-${i}`}>
                <div className="hud-card border border-dashed border-white/8 bg-white/[0.01] p-6 h-full relative overflow-hidden">
                  <div className="absolute inset-0 scanline-bg opacity-30" />
                  <div className="relative z-10">
                    <span className="kicker">Incoming</span>
                    <h3 className="text-xl font-semibold tracking-[-0.02em] mt-2 mb-2 text-white/20">Project {projects.length + i}</h3>
                    <Badge className="bg-white/8 text-white/40 border-white/12">Coming Soon</Badge>
                    <p className="text-sm text-white/25 mt-3 font-mono">Signal detected. Infrastructure preparing.</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Onlenu */}
      {projects.length > 0 && (
        <section className="py-16 sm:py-20 lg:py-28 relative" data-testid="onlenu-highlight-section">
          <div className="absolute inset-0 bg-fire-engine" />
          <div className="absolute inset-0 noise-overlay" />
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-8 lg:gap-10">
              <ScrollReveal>
                <div className="hud-index mb-3">SEC.03 // FEATURED NODE</div>
                <span className="kicker">First Powered Product</span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.04em] leading-[0.95] mt-4 mb-4">{projects[0].name}</h2>
                <p className="text-base text-white/72 leading-relaxed mb-6">{projects[0].description}</p>
                <div className="flex flex-wrap gap-3 mb-8">
                  {(projects[0].features || []).slice(0, 4).map((f) => (
                    <span key={f} className="px-3 py-1.5 rounded-[4px] border border-white/10 bg-white/[0.03] text-xs text-white/60">{f}</span>
                  ))}
                </div>
                <div className="flex gap-3">
                  <a href={projects[0].websiteUrl} target="_blank" rel="noopener noreferrer">
                    <Button className="btn-primary-gradient btn-shimmer font-semibold">Visit {projects[0].name}<ExternalLink className="ml-2 h-4 w-4" /></Button>
                  </a>
                  <Link to={`/powered/${projects[0].slug}`}>
                    <Button variant="outline" className="bg-white/5 text-white/85 border-white/10 hover:bg-white/8">View Details</Button>
                  </Link>
                </div>
              </ScrollReveal>
              <ScrollReveal delay={0.2} direction="right" distance={30}>
                <div className="hud-card border border-white/10 bg-white/[0.03] p-6">
                  <span className="kicker block mb-4">Performance Metrics</span>
                  <div className="space-y-4">
                    <MetricRow label="Status" value={projects[0].status} accent />
                    <MetricRow label="Category" value={projects[0].category || 'SaaS Platform'} />
                    <MetricRow label="Infrastructure" value="Visualte Engine" />
                    <MetricRow label="Uptime" value="99.9%" accent />
                    <MetricRow label="Regions" value="Global" />
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 sm:py-20 lg:py-28 relative" data-testid="cta-section">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <ScrollReveal className="hud-frame py-12 sm:py-16">
            <div className="hud-index mb-4">SEC.04 // INITIATE CONTACT</div>
            <Flame className="h-8 w-8 text-[#E8722A] mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.04em] leading-[0.95] mb-6">Your infrastructure starts here</h2>
            <p className="text-base text-white/72 leading-relaxed max-w-lg mx-auto mb-8">Discuss how Visualte can power your next digital venture.</p>
            <Link to="/contact">
              <Button data-testid="cta-button" className="btn-primary-gradient btn-shimmer font-semibold h-12 px-8 text-base rounded-lg">
                Discuss Business <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </PageTransition>
  );
}

function StatusBadge({ status }) {
  const styles = {
    Live: 'bg-[rgba(232,114,42,0.12)] text-[rgba(240,170,100,0.9)] border-[rgba(232,114,42,0.2)]',
    Beta: 'bg-[rgba(240,170,60,0.10)] text-[rgba(240,190,100,0.88)] border-[rgba(240,170,60,0.18)]',
    'Coming Soon': 'bg-white/8 text-white/70 border-white/12',
  };
  return <Badge className={`${styles[status] || styles['Coming Soon']} text-[10px] font-mono uppercase`}>
    {status === 'Live' && <span className="w-1.5 h-1.5 rounded-full bg-[#E8722A] mr-1.5 status-pulse inline-block" />}{status}
  </Badge>;
}

function MetricRow({ label, value, accent }) {
  return <div className="flex justify-between items-center py-2 border-b border-white/6 last:border-0">
    <span className="text-xs text-white/55">{label}</span>
    <span className={`text-sm font-mono ${accent ? 'text-[#E8722A]' : 'text-white/80'}`}>{value}</span>
  </div>;
}
